import React, { useState, useReducer, useEffect, useCallback, memo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Avatar,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import ModalHeader from '../../../shared/components/ModalHeader';
import { TabPanel } from '../../../shared/components/TabPanel';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import type { EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import {
  useUserManagementService,
  EDITABLE_GROUPS,
} from '../services/userManagementService';
import { RpcError } from '../../../shared/api/rpcClient';
import type { CognitoUser, CedarPolicy } from '../../../types';
import PolicyViewer from './PolicyViewer';

interface UserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DataState {
  tabValue: number;
  users: CognitoUser[];
  selectedUser: CognitoUser | null;
  policies: CedarPolicy[];
  selectedPolicy: CedarPolicy | null;
  pendingGroups: Set<string>;
}

type DataAction =
  | { type: 'SET_TAB'; payload: number }
  | { type: 'SET_USERS'; payload: CognitoUser[] }
  | { type: 'SELECT_USER'; payload: CognitoUser }
  | { type: 'UPDATE_USER'; payload: CognitoUser }
  | { type: 'SET_POLICIES'; payload: CedarPolicy[] }
  | { type: 'SET_SELECTED_POLICY'; payload: CedarPolicy | null }
  | { type: 'TOGGLE_GROUP'; payload: string };

const initialDataState: DataState = {
  tabValue: 0,
  users: [],
  selectedUser: null,
  policies: [],
  selectedPolicy: null,
  pendingGroups: new Set(),
};

function dataReducer(state: DataState, action: DataAction): DataState {
  switch (action.type) {
    case 'SET_TAB':
      return { ...state, tabValue: action.payload };
    case 'SET_USERS':
      return { ...state, users: action.payload };
    case 'SELECT_USER':
      return {
        ...state,
        selectedUser: action.payload,
        pendingGroups: new Set(action.payload.access_groups),
      };
    case 'UPDATE_USER': {
      const updated = action.payload;
      return {
        ...state,
        users: state.users.map((u) => (u.username === updated.username ? updated : u)),
        selectedUser: updated,
        pendingGroups: new Set(updated.access_groups),
      };
    }
    case 'SET_POLICIES':
      return { ...state, policies: action.payload };
    case 'SET_SELECTED_POLICY':
      return { ...state, selectedPolicy: action.payload };
    case 'TOGGLE_GROUP': {
      const next = new Set(state.pendingGroups);
      if (next.has(action.payload)) {
        next.delete(action.payload);
      } else {
        next.add(action.payload);
      }
      return { ...state, pendingGroups: next };
    }
  }
}

const userListConfig: EntityListItemConfig<CognitoUser> = {
  getId: (user) => user.username,
  getPrimaryText: (user) => user.name || user.email,
  getSecondaryText: (user) => user.email,
  getSearchText: (user) => `${user.name} ${user.email}`,
};

const UserManagementModal: React.FC<UserManagementModalProps> = ({ isOpen, onClose }) => {
  const userManagementService = useUserManagementService();

  const [state, dispatch] = useReducer(dataReducer, initialDataState);
  const { tabValue, users, selectedUser, policies, selectedPolicy, pendingGroups } = state;

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // Load users on mount
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);

    try {
      const data = await userManagementService.listUsers();
      dispatch({ type: 'SET_USERS', payload: data });
    } catch (err: unknown) {
      if (err instanceof RpcError && err.code === -32003) {
        setAccessDenied(true);
      } else if (err instanceof RpcError && err.message.includes('not configured')) {
        setError('User management service is not configured.');
      } else {
        setError(err instanceof RpcError ? err.message : 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, [userManagementService]);

  // Load policies
  const loadPolicies = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await userManagementService.listPolicies();
      dispatch({ type: 'SET_POLICIES', payload: data });
    } catch (err: unknown) {
      if (err instanceof RpcError && err.message.includes('not configured')) {
        setError('Policy service is not configured.');
      } else {
        setError(err instanceof RpcError ? err.message : 'Failed to load policies');
      }
    } finally {
      setLoading(false);
    }
  }, [userManagementService]);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen, loadUsers]);

  useEffect(() => {
    if (isOpen && tabValue === 1) {
      loadPolicies();
    }
  }, [isOpen, tabValue, loadPolicies]);

  // Handle user selection
  const handleSelectUser = (user: CognitoUser) => {
    dispatch({ type: 'SELECT_USER', payload: user });
  };

  // Handle group toggle
  const handleGroupToggle = (group: string) => {
    dispatch({ type: 'TOGGLE_GROUP', payload: group });
  };

  // Check if there are unsaved changes
  const hasChanges = selectedUser
    ? JSON.stringify([...pendingGroups].sort()) !==
      JSON.stringify([...selectedUser.access_groups].sort())
    : false;

  // Save changes
  const handleSave = async () => {
    if (!selectedUser || !hasChanges) return;

    setSaving(true);
    setError(null);

    try {
      const currentGroups = new Set(selectedUser.access_groups);
      const addGroups = [...pendingGroups].filter((g) => !currentGroups.has(g));
      const removeGroups = [...currentGroups].filter((g) => !pendingGroups.has(g));

      const updatedUser = await userManagementService.updateUserGroups(selectedUser.username, {
        add_groups: addGroups,
        remove_groups: removeGroups,
      });

      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
    } catch (err: unknown) {
      setError(err instanceof RpcError ? err.message : 'Failed to update user groups');
    } finally {
      setSaving(false);
    }
  };

  // Access denied view
  if (accessDenied) {
    return (
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <ModalHeader icon={PersonIcon} title="User Management" onClose={onClose} />
        <DialogContent>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <LockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Access Denied
            </Typography>
            <Typography color="text.secondary">
              You need admin permissions to manage users.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
      <ModalHeader icon={PersonIcon} title="User Management" onClose={onClose} sx={{ pb: 1 }} desktopRecommended />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => dispatch({ type: 'SET_TAB', payload: v })}>
          <Tab label="Users" />
          <Tab label="Policies" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, height: 500 }}>
        {error && (
          <InlineAlert severity="error" sx={{ m: 2 }}>
            {error}
          </InlineAlert>
        )}

        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', height: '100%' }}>
              {/* User List */}
              <Box sx={{ width: 280, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
                <EntityListPanel
                  entities={users}
                  selectedEntity={selectedUser}
                  isNew={false}
                  saving={false}
                  config={userListConfig}
                  emptyStateText="No users found"
                  emptyStateHelperText="No users are configured"
                  onSelect={handleSelectUser}
                  searchable
                />
              </Box>

              {/* User Details */}
              <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                {selectedUser ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar sx={{ width: 48, height: 48 }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{selectedUser.name || selectedUser.email}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedUser.email}
                        </Typography>
                        <Chip
                          label={selectedUser.status}
                          size="small"
                          color={selectedUser.enabled ? 'success' : 'default'}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>

                    {/* Entra Groups (read-only) */}
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Entra Groups (read-only)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                      {selectedUser.entra_groups.length > 0 ? (
                        selectedUser.entra_groups.map((group) => (
                          <Chip key={group} label={group} size="small" variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </Box>

                    {/* Entra claim, displayed only */}
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Entra Claim Groups (read-only)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                      {(selectedUser.claim_groups ?? []).length > 0 ? (
                        (selectedUser.claim_groups ?? []).map((group) => (
                          <Chip
                            key={group}
                            label={group}
                            size="small"
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          None — the token carries no Entra group claim
                        </Typography>
                      )}
                    </Box>

                    {/* Access Levels (editable) */}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Access Levels (editable)
                    </Typography>
                    <FormGroup sx={{ mb: 3 }}>
                      {EDITABLE_GROUPS.map((group) => (
                        <FormControlLabel
                          key={group}
                          control={
                            <Checkbox
                              checked={pendingGroups.has(group)}
                              onChange={() => handleGroupToggle(group)}
                            />
                          }
                          label={group}
                        />
                      ))}
                    </FormGroup>

                    {/* SSO Groups (read-only) */}
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      SSO Groups (auto-generated)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedUser.sso_groups.length > 0 ? (
                        selectedUser.sso_groups.map((group) => (
                          <Chip key={group} label={group} size="small" variant="outlined" color="default" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </Box>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">Select a user to view details</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <PolicyViewer
            policies={policies}
            selectedPolicy={selectedPolicy}
            onSelectPolicy={(policy) => dispatch({ type: 'SET_SELECTED_POLICY', payload: policy })}
            loading={loading}
            error={error}
          />
        </TabPanel>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {tabValue === 0 && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default memo(UserManagementModal);
